import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Class, User, DeletedClass } from '../types/alarm';
import { loadClasses, saveClasses, loadCurrentClassId, saveCurrentClassId, saveDeletedClass, loadDeletedClasses, restoreDeletedClass, cleanupExpiredDeletedClasses } from '../utils/storage';
import { userDataService } from '../services/userDataService';
import { classCodeService } from '../services/classCodeService';
import ClassSettingsModal from './ClassSettingsModal';
import { checkClassLimit, getLicenseLimits } from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';

interface ClassManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  user: User;
  onClassChange?: (classChangeInfo?: { currentClassId: string; keepModalOpen?: boolean }) => void;
  onMembersChange?: () => void; // 멤버 정보 변경 시 호출될 콜백
  onClassReset?: () => void; // 클래스 초기화 시 호출될 콜백 (캐시 초기화용)
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
  /** 모달 열림과 동시에 이 클래스의 설정 모달을 바로 열기 (메인 페이지 바로가기용) */
  initialClassToOpenSettings?: Class | null;
  /** initialClassToOpenSettings 사용 후 부모에서 초기화할 콜백 */
  onInitialClassSettingsOpened?: () => void;
  /** 메인 페이지에서 현재 선택된 멤버 그룹 (예: 'A', 'B') */
  currentMemberGroup?: string;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  position: relative;
  max-height: 70vh;
  overflow-y: auto;
`;

const Title = styled.h2`
  margin: 0 0 24px 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
`;

const ClassList = styled.div`
  margin-bottom: 24px;
  max-height: 300px;
  overflow-y: auto;
`;

const ClassItem = styled.div<{ $isDisabled?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 8px;
  background: ${props => props.$isDisabled ? 'var(--color-panel-disabled)' : 'var(--color-panel)'};
  transition: all 0.3s ease;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isDisabled ? 0.6 : 1};

  &:hover {
    background: ${props => props.$isDisabled ? 'var(--color-panel-disabled)' : 'var(--color-border)'};
    border-color: ${props => props.$isDisabled ? 'var(--color-border)' : 'var(--color-accent-text)'};
  }

  @media (max-width: 480px) {
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    padding: 12px;
  }
`;

const ClassName = styled.span<{ $isDisabled?: boolean }>`
  font-size: 1rem;
  color: ${props => props.$isDisabled ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'};
  font-weight: 500;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  min-height: 1.4em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
  }
`;

const RadioButton = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-accent-text);
  
  /* 브라우저 기본 포커스 스타일 제거 */
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
  
  /* 모바일에서 다크모드 체크 표시 개선 */
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    
    /* 다크모드에서 체크 표시가 보이도록 */
    :root:not([data-theme='light']) & {
      accent-color: var(--color-accent-text);
      /* 모바일 브라우저의 기본 스타일 오버라이드 */
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      border: 2px solid rgba(255, 255, 255, 0.6);
      border-radius: 50%;
      background: transparent;
      position: relative;
      
      &:checked {
        border-color: var(--color-accent-text);
        background: transparent;
        
        /* 체크 표시를 위한 작은 원 */
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--color-accent-text);
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    
    :root:not([data-theme='light']) & {
      border-width: 2px;
      
      &:checked::after {
        width: 10px;
        height: 10px;
      }
    }
  }
`;



const ClassNameInput = styled.input`
  font-size: 1rem;
  color: var(--color-text-primary);
  font-weight: 500;
  border: 2px solid #6c757d;
  border-radius: 4px;
  padding: 4px 8px;
  background: var(--color-surface);
  outline: none;
  width: 100%;
  min-width: 0;
  flex: 1;
  
  &:focus {
    border-color: #6c757d;
  }
  
  &:focus-visible {
    border-color: #6c757d;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
  
  /* 브라우저 기본 포커스 스타일 제거 */
  &:focus-visible {
    outline: none;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 6px 8px;
  }
`;

const EditButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 2px;
    width: 100%;
    margin-top: 8px;
  }
`;

const EditButton = styled.button<{ isSave?: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isSave ? 'var(--color-accent-strong)' : '#6c757d'};
  color: white;
  white-space: nowrap;
  min-width: 50px;
  
  &:hover {
    background: ${props => props.isSave ? 'var(--color-accent)' : '#5a6268'};
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 8px 6px;
    font-size: 13px;
  }
`;

const AddClassSection = styled.div`
  border-top: 1px solid var(--color-border);
  padding-top: 20px;
`;

const AddClassButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: var(--color-accent-strong);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const AddClassInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 20px;
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  ${props => props.primary ? `
    background: var(--color-accent-strong);
    color: white;

    &:hover {
      background: var(--color-accent);
    }
  ` : `
    background: transparent;
    color: var(--color-accent);
    border: 1px solid var(--color-accent);

    /* 다크모드에서 적절한 색상 사용 */
    :root:not([data-theme='light']) & {
      color: #ffffff;
      border-color: #404040;
    }

    &:hover {
      background: rgba(var(--color-accent-rgb), 0.2);
      border-color: var(--color-accent);
    }
  `}
  
  &:focus-visible {
    outline: ${props => props.primary ? '2px solid var(--color-accent)' : '2px solid var(--color-accent)'};
    outline-offset: 2px;
  }
`;

const ClassActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditIconButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--color-text-secondary);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SettingsIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--color-text-secondary);
  }
`;

const TrashIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--color-text-secondary);
  }
`;

const PlusIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: white;
`;

const WarningMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin: 16px 0;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  text-align: center;
`;

const DeletedClassesSection = styled.div`
  margin-top: 8px;
  padding-top: 0;
`;

const DeletedClassesTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-size: 1rem;
  font-weight: 500;
  padding-left: 4px;
  padding-right: 4px;
`;

const ToggleButton = styled.button`
  background: var(--color-border);
  color: var(--color-text-primary);
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 0;
  
  &:hover {
    background: var(--color-border);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 117, 125, 0.3);
  }
`;

const DeletedClassList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px;
  background: var(--color-panel);
`;

const DeletedClassItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--color-border);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DeletedClassInfo = styled.div`
  flex: 1;
  margin-right: 10px;
  text-align: left;
`;

const DeletedClassGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const DeletedClassName = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-primary);
  font-weight: 400;
  text-align: left;
`;

const DeletedClassDate = styled.span`
  font-size: 0.8rem;
  color: #6c757d;
  text-align: left;
`;

const DaysLeft = styled.span<{ daysLeft: number }>`
  font-size: 0.8rem;
  color: ${props => props.daysLeft > 0 ? 'var(--color-accent-strong)' : '#dc3545'};
  font-weight: 500;
  text-align: left;
  margin-top: 4px;
`;

const RestoreButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const DeleteNowButton = styled.button`
  background: #ff3954;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e0314a;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 57, 84, 0.3);
  }
`;

const DeletedClassButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`;

const ClassManageModal: React.FC<ClassManageModalProps> = ({
  isOpen,
  onClose,
  userId,
  user,
  onClassChange,
  onMembersChange,
  onClassReset,
  onLicenseClick,
  initialClassToOpenSettings,
  onInitialClassSettingsOpened,
  currentMemberGroup
}) => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [editingClassName, setEditingClassName] = useState('');
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [currentClassId, setCurrentClassId] = useState<string>('');
  const [deletedClasses, setDeletedClasses] = useState<DeletedClass[]>([]);
  const [showDeletedClasses, setShowDeletedClasses] = useState(false);
  const [showClassSettings, setShowClassSettings] = useState(false);
  const [selectedClassForSettings, setSelectedClassForSettings] = useState<Class | null>(null);
  const [hasChanges, setHasChanges] = useState(false); // 변경사항 추적
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  /** 메인 페이지 바로가기로 진입했는지 (닫기 시 클래스 관리 모달도 함께 닫기) */
  const openedViaDirectPathRef = useRef(false);

  // 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      openedViaDirectPathRef.current = false;
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // 메인 페이지에서 "클래스 설정 바로 열기"로 진입 시, 현재 클래스 설정 모달 즉시 열기
  useEffect(() => {
    if (!isOpen || !initialClassToOpenSettings) return;
    openedViaDirectPathRef.current = true;
    handleOpenClassSettings(initialClassToOpenSettings);
    onInitialClassSettingsOpened?.();
  }, [isOpen, initialClassToOpenSettings]);

  useEffect(() => {
    if (isOpen) {
      const loadClassData = async () => {
        try {
          console.log('📚 클래스 관리 모달 데이터 로드 시작:', { userId });
          
          // 컬렉션에서 클래스 목록 로드
          const userClasses = await userDataService.loadUserClasses(userId);
          console.log('🔄 컬렉션에서 클래스 로드:', userClasses.length, '개');
          
          setClasses(userClasses);
          
          // 현재 클래스 ID 설정 (user.currentClassId 우선, 없으면 Firestore에서 로드)
          let currentClassIdToSet = user.currentClassId;
          if (!currentClassIdToSet) {
            const savedCurrentClassId = await userDataService.loadCurrentClassId(userId);
            currentClassIdToSet = savedCurrentClassId || '';
            console.log('📡 Firestore에서 현재 클래스 ID 로드:', currentClassIdToSet);
          } else {
            console.log('✅ user 객체에서 현재 클래스 ID 사용:', currentClassIdToSet);
          }
          
          setCurrentClassId(currentClassIdToSet);
          
          // Firestore에서 삭제된 클래스 목록 로드
          const deletedClassesList = await userDataService.loadDeletedClasses(userId);
          console.log('✅ Firestore에서 삭제된 클래스 로드 완료:', deletedClassesList.length, '개');
          setDeletedClasses(deletedClassesList);
          
          // 만료된 삭제된 클래스들 정리 (로컬 스토리지 + Firestore)
          cleanupExpiredDeletedClasses(userId);
          userDataService.cleanupExpiredDeletedClasses(userId).catch(error => {
            console.error('Firestore 만료 클래스 정리 실패:', error);
          });
        } catch (error) {
          console.error('❌ Firestore에서 클래스 데이터 로드 실패:', error);
          // Firestore 실패 시 로컬 스토리지에서 로드
          const userClasses = loadClasses(userId);
          setClasses(userClasses);
          
          // 현재 클래스 ID 설정 (user.currentClassId 우선, 없으면 로컬 스토리지에서 로드)
          let currentClassIdToSet = user.currentClassId;
          if (!currentClassIdToSet) {
            const savedCurrentClassId = loadCurrentClassId(userId);
            currentClassIdToSet = savedCurrentClassId || '';
            console.log('📱 로컬 스토리지에서 현재 클래스 ID 로드:', currentClassIdToSet);
          } else {
            console.log('✅ user 객체에서 현재 클래스 ID 사용:', currentClassIdToSet);
          }
          
          setCurrentClassId(currentClassIdToSet);
          
          const deletedClassesList = loadDeletedClasses(userId);
          setDeletedClasses(deletedClassesList);
          
          // 만료된 삭제된 클래스들 정리 (로컬 스토리지만 - Firestore 실패 시)
          cleanupExpiredDeletedClasses(userId);
        }
      };
      
      loadClassData();
    }
      }, [isOpen, user.currentClassId, userId]);

  const handleClassDoubleClick = (classId: string, className: string) => {
    console.log('🖱️ 클래스 이름 더블클릭:', { classId, className });
    setEditingClassId(classId);
    setEditingClassName(className);
  };

  const handleClassNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('✏️ 클래스 이름 입력 변경:', e.target.value);
    setEditingClassName(e.target.value);
  };

  const handleClassNameSave = async () => {
    console.log('💾 저장 버튼 클릭:', { editingClassId, editingClassName });
    if (editingClassId && editingClassName.trim()) {
      const nameToSave = editingClassName.trim().slice(0, 15);
      const updatedClasses = classes.map(cls => 
        cls.id === editingClassId 
          ? { ...cls, name: nameToSave }
          : cls
      );
      
      try {
        console.log('✏️ 클래스 이름 수정 후 저장 시작:', { userId, classId: editingClassId, newName: nameToSave });
        
        // 개별 클래스 정보 업데이트
        const classToUpdate = updatedClasses.find(cls => cls.id === editingClassId);
        if (classToUpdate) {
          await userDataService.saveClass(userId, classToUpdate);
        }
        
        console.log('✅ 클래스 이름 수정 저장 완료');
        
        setClasses(updatedClasses);
        setEditingClassId(null);
        setEditingClassName('');
        
        // ✅ 변경사항 플래그 설정
        setHasChanges(true);
        
        // ✅ 현재 선택된 클래스의 이름이 변경된 경우 상위 컴포넌트에 알림 (강제 업데이트)
        if (currentClassId === editingClassId) {
          console.log('🔄 클래스 이름 변경으로 인한 강제 업데이트 요청');
          onClassChange?.(); // 매개변수 없이 호출하여 else 블록 실행
        }
      } catch (error) {
        console.error('❌ 클래스 이름 수정 후 Firestore 저장 실패:', error);
        // Firestore 실패 시에도 로컬 상태는 업데이트
        setClasses(updatedClasses);
        setEditingClassId(null);
        setEditingClassName('');
      }
    }
  };

  const handleClassNameCancel = () => {
    setEditingClassId(null);
    setEditingClassName('');
  };

  const handleOpenClassSettings = async (classData: Class) => {
    // 플랜 제한 체크
    const limits = getLicenseLimits(user.userType);
    const maxClasses = limits.maxClasses || 1;

    // 클래스를 생성일 기준으로 정렬 (오래된 순)
    const sortedClasses = [...classes].sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return aTime - bTime; // 오래된 순
    });

    const classIndex = sortedClasses.findIndex(cls => cls.id === classData.id);
    const isDisabled = classIndex >= maxClasses;

    if (isDisabled) {
      // 제한된 클래스의 경우 편집 불가 메시지 표시
      setLicenseLimitMessage('제한된 클래스는 편집이 불가능합니다.');
      setShowLicenseLimitModal(true);
      return;
    }

    try {
      console.log('⚙️ 클래스 설정 모달 열기:', { classId: classData.id, className: classData.name });

      // 최신 클래스 데이터를 Firestore에서 로드
      const latestClassData = await userDataService.loadClass(userId, classData.id);
      if (latestClassData) {
        console.log('📡 최신 클래스 데이터 로드 완료:', { 
          classCode: latestClassData.classCode,
          className: latestClassData.name 
        });
        setSelectedClassForSettings(latestClassData);
      } else {
        console.log('⚠️ 최신 데이터 로드 실패, 기존 데이터 사용');
        setSelectedClassForSettings(classData);
      }
      
      setShowClassSettings(true);
    } catch (error) {
      console.error('❌ 클래스 설정 모달 열기 실패:', error);
      // 실패 시 기존 데이터 사용
      setSelectedClassForSettings(classData);
      setShowClassSettings(true);
    }
  };

  // 메인 페이지 바로가기: 모달 열림 시 현재 클래스 설정 모달을 바로 열기
  useEffect(() => {
    if (!isOpen || !initialClassToOpenSettings) return;
    handleOpenClassSettings(initialClassToOpenSettings);
    onInitialClassSettingsOpened?.();
  }, [isOpen, initialClassToOpenSettings]);

  const handleCloseClassSettings = () => {
    setShowClassSettings(false);
    setSelectedClassForSettings(null);
    if (openedViaDirectPathRef.current) {
      openedViaDirectPathRef.current = false;
      onClose();
    }
  };

  const handleSaveClassSettings = async (updatedClass: Class) => {
    try {
      console.log('💾 클래스 설정 저장 시작:', { 
        classId: updatedClass.id, 
        className: updatedClass.name,
        classCode: updatedClass.classCode,
        memberGroupNames: updatedClass.memberGroupNames
      });
      
      // 1. Firestore에 클래스 정보 저장
      await userDataService.saveClass(userId, updatedClass);
      console.log('✅ Firestore에 클래스 정보 저장 완료');
      
      // 2. 로컬 상태 업데이트
      const updatedClasses = classes.map(cls => 
        cls.id === updatedClass.id ? updatedClass : cls
      );
      setClasses(updatedClasses);
      
      // 3. selectedClassForSettings 업데이트 (모달에서 최신 데이터 반영)
      setSelectedClassForSettings(updatedClass);
      
      // 4. 변경사항 플래그 설정
      setHasChanges(true);
      
      // 5. 현재 클래스가 업데이트된 클래스인 경우 App.tsx에 알림 (currentClass 업데이트용)
      if (currentClassId === updatedClass.id) {
        console.log('🔄 현재 클래스 설정 변경 감지 - App.tsx에 업데이트 요청');
        // onClassChange를 매개변수 없이 호출하면 App.tsx의 else 블록이 실행되어
        // loadClassData를 호출하고 currentClass를 업데이트합니다.
        onClassChange?.();
      }
      
      console.log('✅ 클래스 설정 저장 완료');
      // 모달 닫기는 클래스 설정 모달 내부의
      // "편집 종료" 버튼에서 onClose()를 통해 처리
    } catch (error) {
      console.error('❌ 클래스 설정 저장 실패:', error);
      alert('클래스 설정 저장에 실패했습니다.');
    }
  };

  const getExistingClassCodes = async (): Promise<string[]> => {
    try {
      return await userDataService.getAllClassCodes();
    } catch (error) {
      console.error('전역 클래스 코드 조회 실패:', error);
      // 실패 시 현재 사용자의 클래스 코드만 반환
      return classes
        .map(cls => cls.classCode)
        .filter((code): code is string => !!code);
    }
  };

  const handleAddClass = () => {
    // 라이센스 제한 체크
    const activeClasses = classes.filter(cls => cls.isActive);
    const limitCheck = checkClassLimit(user, activeClasses.length);
    
    if (!limitCheck.allowed) {
      setLicenseLimitMessage(limitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    setIsAddingClass(true);
    setNewClassName('');
  };

  const handleNewClassNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewClassName(e.target.value);
  };

  const handleSaveNewClass = async () => {
    if (newClassName.trim()) {
      try {
        const nameToSave = newClassName.trim().slice(0, 15);
        console.log('📝 새 클래스 생성 시작:', { userId, className: nameToSave });
        
        // 1. 사용 가능한 클래스 코드 즉시 생성
        const classCode = await classCodeService.generateAvailableClassCode();
        
        const newClass: Class = {
          id: `class_${Date.now()}`,
          name: nameToSave,
          classCode: classCode,
          classEntryCode: '0000',
          memberCount: 0,
          createdAt: new Date(),
          isActive: true
        };
        const updatedClasses = [...classes, newClass];
        
        // 2. 컬렉션에 새 클래스 저장 (saveClass에서 자동으로 usedClassCodes에 등록됨)
        await userDataService.saveClass(userId, newClass);
        console.log('✅ 컬렉션에 클래스 저장 완료');
        
        // 3. 새 클래스에 기본 알람셋 생성 (다른 클래스 알람셋과 격리)
        const defaultAlarmSet = {
          id: 'default-alarm-set',
          name: '기본 알람',
          alarms: [],
          isActive: true,
          createdAt: Date.now()
        };
        await userDataService.saveClassAlarmSets(userId, newClass.id, [defaultAlarmSet]);
        console.log('✅ 새 클래스에 기본 알람셋 생성 완료');
        
        // 4. 로컬 상태 업데이트
        setClasses(updatedClasses);
        setCurrentClassId(newClass.id); // 새로 생성된 클래스를 현재 클래스로 설정
        setIsAddingClass(false);
        setNewClassName('');
        setHasChanges(true); // 변경사항 플래그 설정
        
        // 5. 현재 클래스를 새로 생성된 클래스로 변경
        await userDataService.saveCurrentClassId(userId, newClass.id);
        console.log('✅ 현재 클래스를 새 클래스로 변경 완료');
        
        // 클래스 생성 시 헤더 업데이트를 위해 onClassChange 호출하되 모달은 유지
        onClassChange?.({ currentClassId: newClass.id, keepModalOpen: true });
        
        console.log('🎉 새 클래스 생성 완료:', {
          classId: newClass.id,
          className: newClass.name,
          classCode: newClass.classCode,
          currentClassId: newClass.id
        });
      } catch (error) {
        console.error('❌ 클래스 추가 실패:', error);
        alert(error instanceof Error ? error.message : '클래스 생성에 실패했습니다.');
        setIsAddingClass(false);
        setNewClassName('');
      }
    }
  };

  const handleCancelNewClass = () => {
    setIsAddingClass(false);
    setNewClassName('');
  };

  const handleRestoreDeletedClass = async (deletedClass: DeletedClass) => {
    const confirmRestore = window.confirm(
      `"${deletedClass.name}" 클래스를 복구하시겠습니까?\n\n복구 시 해당 클래스의 알람셋과 멤버 데이터도 함께 복구됩니다.`
    );
    
    if (confirmRestore) {
      try {
        console.log('♻️ 클래스와 하위 컬렉션 복원 시작:', { userId, classId: deletedClass.id, className: deletedClass.name });
        
        // 1. deletedClasses에서 classes로 클래스와 하위 컬렉션 복원 (완전한 데이터 복원)
        await userDataService.restoreClassFromDeleted(userId, deletedClass.id);
        
        // 2. 복원된 클래스 목록 다시 로드
        const updatedClasses = await userDataService.loadUserClasses(userId);
        
        console.log('✅ 클래스 복원 완료');
        
        setClasses(updatedClasses);
        saveClasses(userId, updatedClasses);
        
        // 삭제된 클래스 목록에서 제거
        const updatedDeletedClasses = deletedClasses.filter(dc => dc.id !== deletedClass.id);
        setDeletedClasses(updatedDeletedClasses);
        
        // 로컬 스토리지에서 삭제된 클래스 정보 제거
        restoreDeletedClass(userId, deletedClass.id);
        
        alert(`"${deletedClass.name}" 클래스가 성공적으로 복구되었습니다.`);
      } catch (error) {
        console.error('클래스 복구 실패:', error);
        alert('클래스 복구 중 오류가 발생했습니다.');
      }
    }
  };

  const handleDeleteClass = async (classId: string) => {
    if (classes.length <= 1) {
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 3000);
      return;
    }
    
    const confirmDelete = window.confirm(`"${classes.find(cls => cls.id === classId)?.name}" 클래스를 삭제하시겠습니까?\n\n클래스를 삭제하면 14일 후 자동 삭제되며 14일 이전 복구 할 수 있습니다.`);
    
    if (confirmDelete) {
      try {
        const classToDelete = classes.find(cls => cls.id === classId);
        if (!classToDelete) {
          alert('삭제할 클래스를 찾을 수 없습니다.');
          return;
        }
        
        const deletedClass: DeletedClass = {
          ...classToDelete, // 클래스의 모든 정보 포함 (classCode, memberCount 등)
          deletedAt: Date.now(),
          willBeDeletedAt: Date.now() + (14 * 24 * 60 * 60 * 1000) // 14일 후
        };
        
        const updatedClasses = classes.filter(cls => cls.id !== classId);
        
        console.log('🗑️ 클래스와 하위 컬렉션 삭제 시작:', { userId, classId, className: deletedClass.name });
        
        // 1. 클래스와 하위 컬렉션을 deletedClasses로 이동
        await userDataService.moveClassToDeleted(userId, classId, deletedClass);
        
        // 2. 컬렉션 기반이므로 별도 업데이트 불필요
        
        // 3. 서버에서 실제 currentClassId를 다시 로드하여 정확한 상태 확인
        const actualCurrentClassId = await userDataService.loadCurrentClassId(userId);
        console.log('🔍 서버의 실제 currentClassId:', { 
          serverCurrentClassId: actualCurrentClassId,
          modalCurrentClassId: user.currentClassId,
          deletedClassId: classId
        });
        
        // 삭제 후 남은 클래스들 중에서 유효한 클래스 선택
        let newCurrentClassId = '';
        
        if (updatedClasses.length > 0) {
          // 삭제된 클래스가 서버의 현재 클래스였거나, 서버의 현재 클래스가 삭제된 클래스인 경우
          if (actualCurrentClassId === classId || !actualCurrentClassId) {
            // 첫 번째 클래스를 선택
            newCurrentClassId = updatedClasses[0].id;
            console.log('🔄 삭제 후 첫 번째 클래스로 설정:', { 
              deletedClassId: classId, 
              newCurrentClassId 
            });
            
            // 새로운 currentClassId 저장
            await userDataService.saveCurrentClassId(userId, newCurrentClassId);
          } else {
            // 서버의 현재 클래스가 유효한 경우 그대로 유지
            newCurrentClassId = actualCurrentClassId;
            console.log('🔄 서버의 현재 클래스 유지:', { 
              deletedClassId: classId, 
              newCurrentClassId 
            });
          }
        }
        
        // 모달의 체크박스 상태를 업데이트
        setCurrentClassId(newCurrentClassId);
        console.log('✅ 모달 체크박스 상태 업데이트:', { newCurrentClassId });
        
        console.log('✅ 클래스 삭제 완료');
        
        setClasses(updatedClasses);
        setHasChanges(true); // 변경사항 플래그 설정
        
        // 로컬 스토리지에도 백업
        saveDeletedClass(userId, deletedClass);
        
        // 삭제된 클래스 목록 업데이트
        const updatedDeletedClasses = [...deletedClasses, deletedClass];
        setDeletedClasses(updatedDeletedClasses);
        setShowDeletedClasses(true); // 즉시 보기 표시
        
        // currentClassId가 변경된 경우 classChangeInfo와 함께 전달하되 모달은 유지
        if (newCurrentClassId && newCurrentClassId !== user.currentClassId) {
          onClassChange?.({ currentClassId: newCurrentClassId, keepModalOpen: true });
        }
        
        alert(`"${deletedClass.name}" 클래스가 삭제되었습니다. 14일 후 자동으로 완전 삭제됩니다.`);
      } catch (error) {
        console.error('❌ 클래스 삭제 실패:', error);
        alert('클래스 삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleDeleteNow = async (deletedClass: DeletedClass) => {
    const confirmDeleteNow = window.confirm(
      `"${deletedClass.name}" 클래스를 즉시 삭제하시겠습니까?\n\n이 클래스의 모든 알람셋과 멤버 데이터도 함께 완전히 삭제됩니다.`
    );

    if (confirmDeleteNow) {
      try {
        console.log('💀 클래스 즉시 삭제 시작:', { userId, classId: deletedClass.id, className: deletedClass.name });
        
        // deletedClasses에서 클래스와 하위 컬렉션 완전 삭제
        await userDataService.permanentlyDeleteClass(userId, deletedClass.id);
        
        console.log('✅ 클래스 즉시 삭제 완료');

        // 로컬 스토리지에서 삭제된 클래스 정보 완전히 제거
        const { restoreDeletedClass } = await import('../utils/storage');
        restoreDeletedClass(userId, deletedClass.id);

        // 삭제된 클래스 목록에서 제거
        const updatedDeletedClasses = deletedClasses.filter(dc => dc.id !== deletedClass.id);
        setDeletedClasses(updatedDeletedClasses);

        // 즉시 삭제는 이미 삭제된 클래스에 대한 작업이므로 onClassChange 호출하지 않음

        alert(`"${deletedClass.name}" 클래스가 성공적으로 즉시 삭제되었습니다.`);
      } catch (error) {
        console.error('클래스 즉시 삭제 실패:', error);
        alert('클래스 즉시 삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleSaveCurrentClass = async () => {
    if (currentClassId) {
      try {
        await userDataService.saveCurrentClassId(userId, currentClassId);
        
        // 로컬 스토리지에도 백업
        saveCurrentClassId(userId, currentClassId);
        
        // 변경사항이 있으면 홈페이지로 이동, 없으면 현재 페이지 유지
        if (hasChanges) {
          onClassChange?.({ 
            currentClassId, 
            keepModalOpen: false 
          });
        } else {
          onClassChange?.({ currentClassId });
        }
        
        // 모달 닫기
        handleClose();
      } catch (error) {
        console.error('❌ 현재 클래스 변경 후 Firestore 저장 실패:', error);
        // Firestore 실패 시 로컬 스토리지에만 저장
        saveCurrentClassId(userId, currentClassId);
        
        // 변경사항이 있으면 홈페이지로 이동, 없으면 현재 페이지 유지
        if (hasChanges) {
          onClassChange?.({ 
            currentClassId, 
            keepModalOpen: false 
          });
        } else {
          onClassChange?.({ currentClassId });
        }
        
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setEditingClassId(null);
    setEditingClassName('');
    setIsAddingClass(false);
    setNewClassName('');
    setHasChanges(false); // 변경사항 플래그 초기화
    
    // 모달 닫을 때 currentClassId를 초기화하지 않음 (저장된 상태 유지)
    setShowWarning(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay isOpen={isOpen} />
      {!initialClassToOpenSettings && (
      <ModalWrapper>
        <ModalContent>
          <Title>클래스 관리</Title>
          
          <ClassList>
            {showWarning && (
              <WarningMessage>
                클래스는 최소 한 개 이상 있어야 합니다.
              </WarningMessage>
            )}
            {(() => {
              // 플랜별 클래스 운영 제한 적용
              const limits = getLicenseLimits(user.userType);
              const maxClasses = limits.maxClasses || 1; // 기본값 1

              // 클래스를 생성일 기준으로 정렬 (오래된 순)
              const sortedClasses = [...classes].sort((a, b) => {
                const aTime = new Date(a.createdAt || 0).getTime();
                const bTime = new Date(b.createdAt || 0).getTime();
                return aTime - bTime; // 오래된 순
              });

              return sortedClasses.map((cls, index) => {
                const isSelectable = index < maxClasses;
                const isDisabled = !isSelectable;

                return (
                  <ClassItem
                    key={cls.id}
                    onClick={() => {
                      if (isDisabled) {
                        // 선택 불가능한 클래스 클릭 시 경고 메시지
                        setLicenseLimitMessage(`현재 플랜에서는 클래스를 ${maxClasses}개 운영 가능합니다. 플랜을 업그레이드 해주세요.`);
                        setShowLicenseLimitModal(true);
                        return;
                      }
                      if (currentClassId !== cls.id) {
                        setCurrentClassId(cls.id);
                        setHasChanges(true);
                      }
                    }}
                    $isDisabled={isDisabled}
                  >
                  <RadioButton
                    type="radio"
                    name="currentClass"
                    value={cls.id}
                    checked={currentClassId === cls.id}
                    disabled={isDisabled}
                    onChange={(e) => {
                      if (!isDisabled && currentClassId !== e.target.value) {
                        setCurrentClassId(e.target.value);
                        setHasChanges(true);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {editingClassId === cls.id ? (
                    <ClassNameInput
                      value={editingClassName}
                      onChange={handleClassNameChange}
                      autoFocus
                      maxLength={15}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isDisabled}
                    />
                  ) : (
                    <ClassName
                      onDoubleClick={(e) => {
                        if (!isDisabled) {
                          e.stopPropagation();
                          handleClassDoubleClick(cls.id, cls.name);
                        }
                      }}
                      $isDisabled={isDisabled}
                    >
                      {cls.name}
                      {isDisabled && (
                        <DisabledLabel> (플랜 제한)</DisabledLabel>
                      )}
                    </ClassName>
                  )}
                {editingClassId === cls.id ? (
                  <EditButtonGroup onClick={(e) => e.stopPropagation()}>
                    <EditButton isSave onClick={(e) => {
                      e.stopPropagation();
                      handleClassNameSave();
                    }}>
                      저장
                    </EditButton>
                    <EditButton onClick={(e) => {
                      e.stopPropagation();
                      handleClassNameCancel();
                    }}>
                      취소
                    </EditButton>
                  </EditButtonGroup>
                ) : (
                  <ClassActions onClick={(e) => e.stopPropagation()}>
                    <EditIconButton
                      title="클래스 이름 수정"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) {
                          handleClassDoubleClick(cls.id, cls.name);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </EditIconButton>
                    <SettingsIcon
                      viewBox="0 0 24 24"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenClassSettings(cls);
                      }}
                      fill="none"
                    >
                      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </SettingsIcon>
                    <TrashIcon
                      viewBox="0 0 24 24"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClass(cls.id);
                      }}
                      fill="none"
                    >
                      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </TrashIcon>
                  </ClassActions>
                )}
                  </ClassItem>
                );
              });
            })()}
            
            {/* 삭제된 클래스 목록 */}
            {deletedClasses.length > 0 && (
              <DeletedClassesSection>
                <DeletedClassesTitle>
                  삭제된 클래스 ({deletedClasses.length})
                  <ToggleButton onClick={() => setShowDeletedClasses(!showDeletedClasses)}>
                    {showDeletedClasses ? '숨기기' : '보기'}
                  </ToggleButton>
                </DeletedClassesTitle>
                {showDeletedClasses && (
                  <DeletedClassList>
                    {deletedClasses.map(deletedClass => {
                      const daysLeft = Math.ceil((deletedClass.willBeDeletedAt - Date.now()) / (24 * 60 * 60 * 1000));
                      return (
                        <DeletedClassItem key={deletedClass.id}>
                          <DeletedClassInfo>
                            <DeletedClassGroup>
                              <DeletedClassName>{deletedClass.name}</DeletedClassName>
                              <DeletedClassDate>
                                삭제일: {new Date(deletedClass.deletedAt).toLocaleDateString()}
                              </DeletedClassDate>
                            </DeletedClassGroup>
                            <DaysLeft daysLeft={daysLeft}>
                              {daysLeft > 0 ? `${daysLeft}일 후 영구 삭제` : '오늘 영구 삭제'}
                            </DaysLeft>
                          </DeletedClassInfo>
                          <DeletedClassButtons>
                            <RestoreButton onClick={() => handleRestoreDeletedClass(deletedClass)}>
                              복구
                            </RestoreButton>
                            <DeleteNowButton onClick={() => handleDeleteNow(deletedClass)}>
                              즉시 삭제
                            </DeleteNowButton>
                          </DeletedClassButtons>
                        </DeletedClassItem>
                      );
                    })}
                  </DeletedClassList>
                )}
              </DeletedClassesSection>
            )}
          </ClassList>
          
            <AddClassSection>
              {isAddingClass ? (
                <div>
                  <AddClassInput
                    type="text"
                    value={newClassName}
                    onChange={handleNewClassNameChange}
                    placeholder="새 클래스 이름을 입력하세요"
                    autoFocus
                    maxLength={15}
                  />
                <EditButtonGroup>
                  <EditButton isSave onClick={handleSaveNewClass}>
                      저장
                    </EditButton>
                  <EditButton data-variant="modal-secondary" onClick={handleCancelNewClass}>
                      취소
                    </EditButton>
                  </EditButtonGroup>
                </div>
              ) : (
                <AddClassButton onClick={handleAddClass}>
                  <PlusIcon viewBox="0 0 24 24">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </PlusIcon>
                  새 클래스 추가
                </AddClassButton>
              )}
            </AddClassSection>
          
          <ButtonGroup>
            <Button onClick={handleClose}>
              닫기
            </Button>
            <Button primary onClick={handleSaveCurrentClass}>
              확인
            </Button>
          </ButtonGroup>
        </ModalContent>
      </ModalWrapper>
      )}
      
      {/* 클래스 설정 모달 */}
      {selectedClassForSettings && (
        <ClassSettingsModal
          isOpen={showClassSettings}
          classData={selectedClassForSettings}
          userId={userId}
          user={user}
          currentMemberGroup={currentMemberGroup}
          onClose={handleCloseClassSettings}
          onSave={handleSaveClassSettings}
          getExistingClassCodes={getExistingClassCodes}
          onMembersChange={() => setHasChanges(true)}
          onClassReset={onClassReset}
          onLicenseClick={onLicenseClick}
        />
      )}
      
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />
    </>
  );
};

const DisabledLabel = styled.span`
  font-size: 0.8rem;
  color: var(--color-accent-strong);
  font-weight: 600;
  margin-left: 8px;
`;

export default ClassManageModal; 